import React, { useState, useMemo } from 'react';
import DataTable from 'react-data-table-component';
import Buttons from '../../components/Button';
import { UserInput } from '../../interfaces/user';
import { useNavigate } from 'react-router-dom';
import {deleteUser} from '../../graphql/api/userApi';
import SearchInput from '../../components/SearchInput';
import NoData from '../../components/NoData';

interface UserManagementProps {
  isEditing: boolean;
  setIsEditing: (editing: boolean) => void;
  setActiveTab: (tab: 'user' | 'vendor' | 'shop') => void;
  setUserForm: (form: any) => void;
  users: UserInput[];
  setUsers: (users: UserInput[]) => void;
}

const UserManagement: React.FC<UserManagementProps> = ({
  isEditing,
  setIsEditing,
  setActiveTab,
  setUserForm,
  users,
  setUsers
}) => {
  const [searchText, setSearchText] = useState('');
  const navigate = useNavigate();

const filteredUsers = useMemo(() => {
  const searchTerm = searchText.toLowerCase();
  return users.filter(user =>
    (user.username?.toLowerCase() ?? '').includes(searchTerm) ||
    (user.email?.toLowerCase() ?? '').includes(searchTerm) ||
    (user.role?.toLowerCase() ?? '').includes(searchTerm) ||
    (user.status?.toLowerCase() ?? '').includes(searchTerm)
  );
}, [users, searchText]);

  const handleEdit = (user: UserInput) => {
    setUserForm({
      id: user.id,
      username: user.username,
      email: user.email,
      phoneNumber: user.phoneNumber,
      role: user.role,
      status: user.status,
      password: '',
      gender: user.gender || ''
    });
    setIsEditing(true);
    setActiveTab('user');
  };


  const userColumns = [
    {
      name: 'Name',
      selector: (row: UserInput) => row.username,
      sortable: true,
    },
    {
      name: 'Email',
      selector: (row: UserInput) => row.email,
      sortable: true,
    },
    {
      name: 'Role',
      cell: (row: UserInput) => (
        <span
          className="clickable-role"
          onClick={() => {
            if (row.role === 'user') {
              navigate(`/profile/${row.id}`);
            } else if (row.role === 'vendor') {
              navigate(`/vendorprofile/${row.id}`);
            }
          }}
          style={{ cursor: 'pointer', textDecoration: 'underline', color: 'blue' }}
        >
          {row.role}
        </span>
      ),
      sortable: true,
    },
    {
      name: 'Status',
      selector: (row: UserInput) => row.status,
      sortable: true,
      cell: (row: UserInput) => (
        <span className={`status-badge ${row.status === 'active' ? 'active' : 'inactive'}`}>
          {row.status}
        </span>
      ),
    },
    {
      name: 'Actions',
      cell: (row: UserInput) => (
        <div className="action-buttons">
          <Buttons
            text="Edit"
            onClick={() => handleEdit(row)}
            variant="edit"
          />
          <Buttons
            text="Delete"
            onClick={() => deleteUser(row.id)}
            variant="delete"
          />
        </div>
      ),
      ignoreRowClick: true,
      allowOverflow: true,
      button: true,
    },
  ];

  return (
    <div>
      <div className="mb-3">

        <SearchInput searchText={searchText} setSearchText={setSearchText} />
      </div>

      <DataTable
        columns={userColumns}
        data={filteredUsers}
        pagination
        highlightOnHover
        striped
        responsive
         paginationPerPage={5}
        noDataComponent={<NoData message='No users found' />}
      />
    </div>
  );
};

export default UserManagement;